'use client';

import { useEffect, useState } from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  msUntilNextAeBoundary,
  resolveAeDeliveryWindow,
  type AeDeliveryWindow,
} from './deliveryWindow';

/**
 * Live Asia/Dubai delivery window (see ./deliveryWindow.ts), re-synced via a
 * timeout armed to the next GST boundary (10:00 / 18:00 / midnight) instead
 * of polling, plus a `visibilitychange` re-sync so a laptop waking past a
 * boundary corrects immediately rather than waiting for the timer. Called by
 * AeDeliveryEta and both bottom bars — state is NOT shared between them: each
 * instance owns its own state and timer. They agree on the window because
 * `resolveAeDeliveryWindow` is a pure function of wall-clock time, not
 * because of any shared store.
 *
 * Hydration safety: `serverNow` is stamped once per request
 * (lib/region.server.ts) and flows through RegionLocaleContext identically
 * to server render and first client paint, so the initial window never
 * mismatches.
 *
 * @param enabled - Pass the caller's AE visibility gate. When false, the
 * effect returns immediately: no timer, no `visibilitychange` listener, no
 * re-render risk for non-AE visitors. The hook itself must still be called
 * unconditionally (rules of hooks) — only its internal effect is gated.
 */
export const useAeDeliveryWindow = (enabled: boolean): AeDeliveryWindow => {
  const { serverNow } = useRegionLocale();
  const [deliveryWindow, setDeliveryWindow] = useState<AeDeliveryWindow>(() =>
    resolveAeDeliveryWindow(new Date(serverNow)),
  );

  useEffect(() => {
    if (!enabled) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      clearTimeout(timeoutId);
      const now = new Date();
      setDeliveryWindow(resolveAeDeliveryWindow(now));
      timeoutId = setTimeout(sync, msUntilNextAeBoundary(now));
    };
    sync();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);

  return deliveryWindow;
};
